/* RWDY scroll animations — GSAP + ScrollTrigger.

   Safe by design: if GSAP is missing or the visitor prefers reduced motion,
   nothing is hidden and the page renders exactly as before.

   NOTE: it is NOT enough that "content is visible if JS never runs". The real
   risk is the middle case — GSAP loads, gsap.from() paints every target to
   opacity 0, and then the tween never advances (suspended requestAnimationFrame
   in a background tab or an offscreen WebView, a stalled ScrollTrigger, a slow
   device). The content is then invisible forever. Two guards below handle this:
     1. withTrigger()  — skips ScrollTrigger when its start is already unreachable
     2. track()/sweep() — forces anything past its reveal line back to visible
   Any new reveal added to this file MUST call track() on its targets. */
(function () {
  "use strict";
  function init() {
    try {
      if (!window.gsap || !window.ScrollTrigger) return;
      if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      gsap.registerPlugin(ScrollTrigger);
      var $ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

      /* ------------------------------------------------------------------
         BUGFIX — elements that are ALREADY past the start line on page load.

         "top 82%" means: fire when the trigger's top reaches 82% down the
         viewport. If the element is already ABOVE that line when the page
         loads (e.g. the collection-page product grid, which starts ~44% down),
         ScrollTrigger computes a NEGATIVE start — a scroll position that can
         never be reached scrolling down — so onEnter never fires. Because
         gsap.from() has already applied the "from" state, the element stays at
         opacity 0 permanently. On /collections/all this left the entire first
         row of products invisible until the visitor happened to scroll.

         Fix: if the trigger is already past the line at load, run the tween
         immediately with no ScrollTrigger attached.
         ------------------------------------------------------------------ */
      var startPct = function (startStr) {
        var part = String(startStr || "").split(/\s+/)[1] || "";
        if (part.slice(-1) !== "%") return null;      // e.g. "top top" — not a % start
        var n = parseFloat(part);
        return isNaN(n) ? null : n / 100;
      };
      var alreadyPastStart = function (el, startStr) {
        var pct = startPct(startStr);
        if (pct === null || !el || !el.getBoundingClientRect) return false;
        return el.getBoundingClientRect().top < window.innerHeight * pct;
      };
      // Builds tween vars, attaching scrollTrigger ONLY when it can still fire.
      var withTrigger = function (vars, trigger, startStr) {
        if (trigger && !alreadyPastStart(trigger, startStr)) {
          vars.scrollTrigger = { trigger: trigger, start: startStr, once: true };
        }
        return vars;
      };

      /* ------------------------------------------------------------------
         FAILSAFE — never leave content permanently invisible.

         gsap.from() applies its "from" state (opacity 0) to the element
         immediately, then animates out of it on the requestAnimationFrame
         ticker. If that ticker never runs, the element stays invisible for
         good. The header comment above claims content is safe "even if JS
         never runs", but the dangerous case is the opposite: GSAP DOES load,
         paints everything to opacity 0, and then stalls.

         Seen for real: a page loaded in a background tab has rAF suspended, so
         gsap.ticker.frame stayed at 0 and 40 elements on the homepage were
         frozen at opacity 0 — including the entire brand-story copy column,
         which made the section render as a lone image with blank space beside
         it. The same applies to a WebView that isn't visible while it loads.

         So: track everything we hide, and sweep. Anything that has reached its
         reveal line but is still transparent gets forced visible.
         ------------------------------------------------------------------ */
      var revealed = [];
      function track(t) {
        if (!t) return;
        (t.length !== undefined && !t.nodeType ? Array.prototype.slice.call(t) : [t])
          .forEach(function (el) { if (el && el.nodeType === 1) revealed.push(el); });
      }
      function sweep() {
        var vh = window.innerHeight || 0;
        for (var i = 0; i < revealed.length; i++) {
          var el = revealed[i];
          if (!el || !el.isConnected) continue;
          var r = el.getBoundingClientRect();
          // Only force elements that should already have revealed, so content
          // further down the page keeps its animation.
          if (r.top > vh * 0.95) continue;
          var cs = window.getComputedStyle(el);
          if (parseFloat(cs.opacity) < 0.99) {
            gsap.set(el, { clearProps: "opacity,transform,visibility" });
          }
        }
      }
      // Run when the tab/WebView becomes visible again (rAF resumes here),
      // while scrolling, and on a short leash after load.
      document.addEventListener("visibilitychange", function () {
        if (document.visibilityState === "visible") {
          try { ScrollTrigger.refresh(); } catch (e) {}
          setTimeout(sweep, 400);
        }
      });
      window.addEventListener("scroll", function () {
        clearTimeout(window.__rwdySweepT);
        window.__rwdySweepT = setTimeout(sweep, 250);
      }, { passive: true });
      var sweeps = 0;
      var sweepTimer = setInterval(function () {
        sweep();
        if (++sweeps > 12) clearInterval(sweepTimer);   // ~6s, then stop
      }, 500);

      var reveal = function (el, opts) {
        opts = opts || {};
        var startStr = opts.start || "top 88%";
        var trigger = opts.trigger || el;
        if (typeof trigger === "string") trigger = document.querySelector(trigger);
        track(el);
        gsap.from(el, withTrigger({
          opacity: 0, y: opts.y != null ? opts.y : 34, duration: 0.7, ease: "power3.out",
          stagger: opts.stagger || 0
        }, trigger, startStr));
      };

      /* ---- HERO: parallax background + word-by-word heading ---- */
      var heroImg = document.querySelector(".hero-banner__img");
      if (heroImg) {
        gsap.to(heroImg, { yPercent: 10, ease: "none",
          scrollTrigger: { trigger: ".hero-banner", start: "top top", end: "bottom top", scrub: true } });
      }
      splitWordsReveal(".hero-banner__heading", { onload: true });
      $(".hero-banner__kicker, .hero-banner__cta-wrap").forEach(function (el) {
        track(el);
        gsap.from(el, { opacity: 0, y: 20, duration: 0.8, ease: "power2.out", delay: 0.5 });
      });

      /* ---- Generic reveals across every section ---- */
      var revealSelectors = [
        ".section-header__title", ".section-header__link",
        ".vd-statements__label", ".vd-statements__heading", ".vd-statements__body",
        ".vd-statements__right-img",
        ".rwdy-vault__label", ".rwdy-vault__heading", ".rwdy-vault__body",
        ".brand-story__label", ".brand-story__heading", ".brand-story__body",
        ".brand-banner__sub", ".spotted-by-rwdy__hint",
        ".drop-diaries__caption", ".newsletter-strip__copy", ".newsletter-strip__form",
        ".the-drop__card", ".rwdy-frames__inner", ".rwdy-club-section__stat"
      ];
      $(revealSelectors.join(",")).forEach(function (el) { reveal(el); });

      /* Buttons inside content sections (skip hero, handled above) */
      $(".vd-statements .btn, .rwdy-vault .btn, .brand-story .btn, .rwdy-club-section .btn").forEach(function (el) {
        reveal(el, { y: 16 });
      });

      /* ---- Staggered grids ---- */
      [
        [".product-grid", ".product-card"],
        [".spotted-grid", ".spotted-item"],
        [".drop-diaries__grid", ".drop-diaries__item"],
        [".ugc-grid", ".ugc-grid__item"],
        [".rwdy-vault__stats", "> *"],
        [".rwdy-club-section__stats", "> *"]
      ].forEach(function (pair) {
        $(pair[0]).forEach(function (grid) {
          var items = pair[1] === "> *" ? Array.prototype.slice.call(grid.children) : $(pair[1], grid);
          if (!items.length) return;
          track(items);
          gsap.from(items, withTrigger(
            { opacity: 0, y: 34, duration: 0.7, ease: "power3.out", stagger: 0.08 },
            grid, "top 82%"));
        });
      });

      /* ---- Editorial image parallax ---- */
      [
        [".vd-statements__main-img", ".vd-statements"],
        [".rwdy-vault__img", ".rwdy-vault"],
        [".brand-story__img", ".brand-story"]
      ].forEach(function (p) {
        var el = document.querySelector(p[0]);
        if (el) gsap.to(el, { yPercent: 8, ease: "none",
          scrollTrigger: { trigger: p[1], start: "top bottom", end: "bottom top", scrub: true } });
      });

      /* ---- Brand banner: word reveal on scroll ---- */
      splitWordsReveal(".brand-banner__heading", { trigger: ".brand-banner", start: "top 80%" });

      /* ---- Stat count-up (numeric only, non-destructive) ---- */
      $(".rwdy-vault__stat-num, .rwdy-club-section__stat-num").forEach(function (el) {
        var raw = (el.textContent || "").trim();
        var m = raw.replace(/,/g, "").match(/^(\d+(?:\.\d+)?)([^\d].*)?$/);
        if (!m) return;                       // not a plain number — leave it alone
        var target = parseFloat(m[1]);
        var suffix = m[2] || "";
        var decimals = (m[1].split(".")[1] || "").length;
        var o = { v: 0 };
        ScrollTrigger.create({ trigger: el, start: "top 88%", once: true, onEnter: function () {
          gsap.to(o, { v: target, duration: 1.5, ease: "power2.out", onUpdate: function () {
            el.textContent = o.v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
          }, onComplete: function () { el.textContent = raw; } });
        } });
      });

      function splitWordsReveal(sel, opts) {
        opts = opts || {};
        var h = document.querySelector(sel);
        if (!h || h.dataset.rwdySplit) return;
        h.dataset.rwdySplit = "1";
        var words = h.textContent.trim().split(/\s+/);
        h.innerHTML = words.map(function (w) {
          return '<span style="display:inline-block;overflow:hidden;vertical-align:top">' +
                 '<span style="display:inline-block">' + w + "</span></span>";
        }).join(" ");
        var inner = h.querySelectorAll("span > span");
        track(inner);
        if (opts.onload) {
          gsap.from(inner, { yPercent: 110, duration: 1, ease: "power4.out", stagger: 0.1, delay: 0.15 });
        } else {
          var startStr = opts.start || "top 82%";
          var trigger = opts.trigger || h;
          if (typeof trigger === "string") trigger = document.querySelector(trigger);
          gsap.from(inner, withTrigger(
            { yPercent: 110, duration: 0.9, ease: "power4.out", stagger: 0.04 },
            trigger, startStr));
        }
      }
    } catch (e) { /* never break the storefront over an animation */ }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
